import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { useUpdatePlannedOrder, type PlannedOrder } from "@/api/planningRun";
import { useItemSources } from "@/api/sources";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDate } from "@/utils/formatters";

import { ExistingOrderTerms } from "./ExistingOrderTerms";

/** Editing firms the order. An open order keeps its source. */
export function OrderEditDialog({
  order,
  onClose,
}: {
  order: PlannedOrder;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdatePlannedOrder();
  const sources = useItemSources(order.item);
  const openOrder = order.sourceExecution != null;
  const schema = z.object({
    quantity: z
      .number()
      .positive(
        t("planningReview.quantityPositive", "Enter a quantity above zero."),
      ),
    dueDate: z.string().min(1),
    source: z.number(),
  });
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      quantity: Number(order.quantity),
      dueDate: order.dueDate,
      source: order.source,
    },
  });
  const submit = form.handleSubmit(({ quantity, dueDate, source }) =>
    update.mutate(
      {
        run: order.run,
        id: order.id,
        status: "firmed",
        quantity: String(quantity),
        dueDate,
        source,
      },
      { onSuccess: onClose },
    ),
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("planningReview.editOrder", "Edit order")}
          </DialogTitle>
          <DialogDescription>
            {order.itemSku} · {order.itemName} · {order.locationName}
          </DialogDescription>
        </DialogHeader>
        <ExistingOrderTerms order={order} />
        <Form {...form}>
          <form id="edit-order" onSubmit={submit} className="space-y-4">
            <FormField
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("planningReview.quantity", "Quantity")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="any"
                      {...field}
                      onChange={(e) => field.onChange(e.target.valueAsNumber)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="dueDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("planningReview.arrival", "Arrival")}
                  </FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  {order.suggestedDueDate && (
                    <FormDescription>
                      {t(
                        "planningReview.suggestedArrival",
                        "Suggested arrival {{date}}",
                        { date: formatDate(order.suggestedDueDate) },
                      )}
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="source"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("planningReview.source", "Source")}</FormLabel>
                  <Select
                    value={String(field.value)}
                    onValueChange={(value) => field.onChange(Number(value))}
                    disabled={openOrder}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {sources.data
                        ?.filter((s) => s.location === order.location)
                        .map((s) => (
                          <SelectItem key={s.id} value={String(s.id)}>
                            {s.code} · {s.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
        {update.isError && (
          <p role="alert" className="text-destructive text-sm">
            {t(
              "planningReview.saveFailed",
              "Could not save this order. Reload the plan and try again.",
            )}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.buttons.cancel", "Cancel")}
          </Button>
          <Button type="submit" form="edit-order" disabled={update.isPending}>
            {t("planningReview.saveAndAccept", "Save and accept")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
